import type { ComponentProps } from 'react'
import React from 'react'
import { Refresh, Square } from '@mui/icons-material'
import { Box, Button, Typography } from '@mui/material'
import GLOBAL from 'modules/global'

type Variant = 'no-data' | 'error'

const VARIANTS: Record<
  Variant,
  {
    icon: string
    title: string
    description: string
    buttonLabel: string
    color: ComponentProps<typeof Button>['color']
  }
> = {
  'no-data': {
    icon: 'MonitorHeartOutlined',
    title: 'No Data Available',
    description: 'There is currently no data to display.',
    buttonLabel: 'Refresh',
    color: 'inherit'
  },
  error: {
    icon: 'MonitorHeartOutlined',
    title: 'Failed to Load',
    description: 'Something went wrong. Please try again.',
    buttonLabel: 'Retry',
    color: 'error'
  }
}

interface NoDataAvailableProps {
  onRefresh?: () => void
  variant?: Variant
}

const NoDataAvailable: React.FC<NoDataAvailableProps> = ({ onRefresh, variant = 'no-data' }) => {
  const { icon, title, description, buttonLabel, color } = VARIANTS[variant]
  const IconComponent = GLOBAL.icon(icon)

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        height: 300,
        gap: 2
      }}
    >
      <Box
        sx={{
          width: 80,
          height: 80,
          borderRadius: 6,
          backgroundColor: color,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          mb: 1
        }}
      >
        <Square
          color={color}
          sx={{ fontSize: 120, position: 'absolute', opacity: 0.1, borderRadius: 40 }}
        />
        <IconComponent color={color} sx={{ fontSize: 48 }} />
      </Box>
      <Typography variant='h6' color='textPrimary' sx={{ fontWeight: 500 }}>
        {title}
      </Typography>
      <Typography variant='body1' color='textSecondary'>
        {description}
      </Typography>
      {onRefresh && (
        <Button
          data-download-hide='true'
          startIcon={<Refresh />}
          onClick={onRefresh}
          variant='contained'
          color='inherit'
          sx={{ mt: 2 }}
        >
          {buttonLabel}
        </Button>
      )}
    </Box>
  )
}

export default NoDataAvailable
